'use client';

import { TabOverflowArrows, type TabOverflowLabels, useTabOverflow } from 'BomdisoftComponents/tab/overflow';
import 'BomdisoftComponents/tab/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import type { KeyboardEvent, ReactNode } from 'react';

export type UnderlineTab<Value extends string = string> = { label: ReactNode; value: Value };

export type UnderlineTabsProps<Value extends string = string> = {
  /** Id of the tabpanel every tab controls; the consumer renders that panel. */
  ariaControls?: string;
  className?: string;
  label: string;
  onChange: (value: Value) => void;
  /** Translated names for the scroll chevrons, shown when the row overflows. */
  scrollLabels?: TabOverflowLabels;
  tabs: readonly UnderlineTab<Value>[];
  /** Each tab answers `${testIdPrefix}-${value}`. */
  testIdPrefix: string;
  value: Value;
};

/**
 * The console's underline tab row as ONE controlled component, replacing the
 * per-page hand-rolled tablists that drifted apart (the messages page shipped
 * an active style keyed on an attribute its buttons had stopped carrying,
 * which is exactly the drift a shared component exists to prevent). The active
 * value belongs to the caller, so it can live in the URL.
 */
export const UnderlineTabs = <Value extends string>({ ariaControls, className, label, onChange, scrollLabels, tabs, testIdPrefix, value }: UnderlineTabsProps<Value>): ReactNode => {
  const { frameProps, page, rowProps, scrollerClassName } = useTabOverflow({ selected: value });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex(tab => tab.value === value);
    const moves: Record<string, number> = { ArrowLeft: at - 1, ArrowRight: at + 1, End: tabs.length - 1, Home: 0 };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    onChange(target.value);
    const buttons = rowProps.ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    const moved = buttons?.[(next + tabs.length) % tabs.length];
    moved?.focus();
    /* Focus alone does not scroll a horizontally clipped row far enough to
     * clear the fade, so the arrow keys would walk the focus ring under it. */
    moved?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  return (
    <div {...frameProps}>
      <div aria-label={label} className={joinClasses('bomdisoft-underline-tabs', scrollerClassName, className)} onKeyDown={onKeyDown} role='tablist' {...rowProps}>
        {tabs.map(tab => (
          <button aria-controls={ariaControls} aria-selected={tab.value === value} className='bomdisoft-underline-tabs-tab' data-testid={`${testIdPrefix}-${tab.value}`} key={tab.value} onClick={() => onChange(tab.value)} role='tab' tabIndex={tab.value === value ? 0 : -1} type='button'>
            {tab.label}
          </button>
        ))}
      </div>
      <TabOverflowArrows labels={scrollLabels} onPage={page} />
    </div>
  );
};
